import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { authenticatedFetch, getAuthHeaders } from '../utils/auth.js'
import StoreSelector from './StoreSelector.jsx'
import { SettingsFormCard } from '../design-system/patterns/index.js'
import { Button, Input } from '../design-system/primitives/index.js'

function safeStringify(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2)
  } catch {
    return '{}'
  }
}

function normalizePreferences(prefs) {
  const p = prefs && typeof prefs === 'object' && !Array.isArray(prefs) ? { ...prefs } : {}
  if (!p.dietary || typeof p.dietary !== 'object') p.dietary = {}
  if (!p.shopping || typeof p.shopping !== 'object') p.shopping = {}
  if (!p.recipe || typeof p.recipe !== 'object') p.recipe = {}
  if (!p.family_members || typeof p.family_members !== 'object') p.family_members = {}
  return p
}

const RECIPE_TIME_KEYS = [
  'max_total_time_minutes',
  'max_time_minutes',
  'time_limit_minutes',
  'total_time_max_minutes',
  'total_time_minutes_max',
  'max_cook_time_minutes',
  'cook_time_max_minutes',
]

function getFirstNumber(obj, key) {
  if (!obj || typeof obj !== 'object') return null
  const v = obj[key]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

function readRecipeMaxMinutes(recipeObj) {
  for (const k of RECIPE_TIME_KEYS) {
    const n = getFirstNumber(recipeObj, k)
    if (n != null) return { minutes: n, sourceKey: k }
  }
  return { minutes: null, sourceKey: null }
}

function writeRecipeMaxMinutes(recipeObj, minutes) {
  const next = recipeObj && typeof recipeObj === 'object' ? { ...recipeObj } : {}
  // Normalize: store as canonical max_total_time_minutes
  if (minutes == null || minutes === '') {
    for (const k of RECIPE_TIME_KEYS) delete next[k]
    return next
  }
  const n = Number(minutes)
  if (!Number.isFinite(n)) return next
  // Remove alternate keys to avoid ambiguity
  for (const k of RECIPE_TIME_KEYS) {
    if (k !== 'max_total_time_minutes') delete next[k]
  }
  next.max_total_time_minutes = Math.max(1, Math.round(n))
  return next
}

function readCuisinePreferences(recipeObj) {
  if (!recipeObj || typeof recipeObj !== 'object') return []
  const v = recipeObj.cuisine_preferences ?? recipeObj.preferred_cuisines ?? recipeObj.cuisines
  if (Array.isArray(v)) return v.filter(x => typeof x === 'string' && x.trim())
  return []
}

function writeCuisinePreferences(recipeObj, values) {
  const next = recipeObj && typeof recipeObj === 'object' ? { ...recipeObj } : {}
  const normalized = Array.isArray(values) ? values.filter(x => typeof x === 'string' && x.trim()) : []
  next.cuisine_preferences = normalized
  // Don't delete other aliases; just standardize on cuisine_preferences
  return next
}

/** Cuisines shown as toggles (prototype + a few extras). */
const RECIPE_CUISINE_TOGGLES = [
  'American',
  'Asian',
  'French',
  'Greek',
  'Indian',
  'Italian',
  'Mediterranean',
  'Mexican',
  'Spanish',
  'Thai',
  'Vietnamese',
]

function readWeeknight(recipeObj) {
  if (!recipeObj || typeof recipeObj !== 'object') return false
  return Boolean(recipeObj.prefer_weeknight_friendly || recipeObj.weeknight_friendly)
}

function writeWeeknight(recipeObj, on) {
  const next = recipeObj && typeof recipeObj === 'object' ? { ...recipeObj } : {}
  next.prefer_weeknight_friendly = Boolean(on)
  return next
}

function cuisineListHas(list, name) {
  const arr = Array.isArray(list) ? list : []
  return arr.some((s) => typeof s === 'string' && s.toLowerCase() === name.toLowerCase())
}

function toggleCuisineInList(list, name, enabled) {
  const arr = Array.isArray(list) ? [...list] : []
  const filtered = arr.filter(
    (s) => typeof s !== 'string' || s.toLowerCase() !== name.toLowerCase(),
  )
  if (enabled) filtered.push(name)
  return filtered
}

function dietBadgesForDisplay(dietary) {
  const d = dietary && typeof dietary === 'object' ? dietary : {}
  const badges = []
  if (d.vegetarian) {
    badges.push({ key: 'veg', cls: 'account-diet-badge account-diet-badge-veg', text: 'Vegetarian' })
  }
  if (d.vegan) {
    badges.push({ key: 'vegan', cls: 'account-diet-badge account-diet-badge-veg', text: 'Vegan' })
  }
  if (d.gluten_free) {
    badges.push({ key: 'gf', cls: 'account-diet-badge account-diet-badge-gf', text: 'GF' })
  }
  const allergies = Array.isArray(d.allergies) ? d.allergies.filter(Boolean) : []
  if (allergies.length) {
    const t = allergies[0]
    badges.push({
      key: 'al',
      cls: 'account-diet-badge account-diet-badge-nf',
      text: t.length > 14 ? 'Allergies' : t,
    })
  }
  if (badges.length === 0) {
    badges.push({ key: 'none', cls: 'account-diet-badge account-diet-badge-none', text: 'No restrictions' })
  }
  return badges
}

function TagInput({ label, values, onChange, placeholder }) {
  const [input, setInput] = useState('')

  const normalized = Array.isArray(values) ? values.filter(v => typeof v === 'string' && v.trim()) : []

  function add() {
    const v = input.trim()
    if (!v) return
    if (normalized.some(x => x.toLowerCase() === v.toLowerCase())) {
      setInput('')
      return
    }
    onChange([...normalized, v])
    setInput('')
  }

  return (
    <div className="recipe-prefs-field-block">
      <div className="recipe-prefs-label">{label}</div>
      <div className="preferences-tag-input-row">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder={placeholder}
          className="preferences-tag-input-field"
          aria-label={label}
        />
        <Button type="button" variant="secondary" onClick={add} aria-label={`Add ${label}`}>
          Add
        </Button>
      </div>
      {normalized.length > 0 && (
        <div className="recipe-prefs-tag-row">
          {normalized.map((v) => (
            <span key={v} className="preferences-tag">
              {v}
              <button
                type="button"
                className="preferences-tag-remove"
                onClick={() => onChange(normalized.filter(x => x !== v))}
                aria-label={`Remove ${v}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function HouseholdMembersPanel({
  familyMembers,
  onChange,
  user,
  isAdmin,
  youDietary,
  selected,
  onSelectedChange,
}) {
  const members = familyMembers && typeof familyMembers === 'object' ? familyMembers : {}
  const [showAdd, setShowAdd] = useState(false)
  const [newName, setNewName] = useState('')
  const [localError, setLocalError] = useState('')

  const youName = (user?.first_name || '').trim() || (user?.email || '').split('@')[0] || 'You'
  const youRole = isAdmin ? 'You (admin)' : 'You'

  function addMember() {
    const name = newName.trim()
    if (!name) return
    if (members[name]) {
      setLocalError('That family member already exists.')
      return
    }
    setLocalError('')
    onChange({
      ...members,
      [name]: {
        dietary: {},
        recipe: {},
        shopping: {},
        general: {},
      },
    })
    setNewName('')
    setShowAdd(false)
    onSelectedChange(name)
  }

  function updateMember(name, updater) {
    const current = members[name] || {}
    const nextMember = updater({ ...(current || {}) })
    onChange({ ...members, [name]: nextMember })
  }

  function deleteMember(name) {
    const next = { ...members }
    delete next[name]
    onChange(next)
    if (selected === name) onSelectedChange('you')
  }

  const memberKeys = Object.keys(members)

  return (
    <div className="account-prefs-form-card">
      <div className="account-prefs-form-card-header">
        <div className="account-prefs-form-card-title">
          <i className="fas fa-users" aria-hidden="true" />
          Household Members
        </div>
        <Button
          type="button"
          variant="secondary"
          className="preferences-household-add-btn"
          onClick={() => {
            setShowAdd((v) => !v)
            setLocalError('')
          }}
          aria-expanded={showAdd}
        >
          <i className="fas fa-plus" aria-hidden="true" /> Add person
        </Button>
      </div>
      <div className="account-prefs-form-card-body">
        {showAdd ? (
          <div className="preferences-household-add-row">
            <Input
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value)
                setLocalError('')
              }}
              placeholder="Name"
              className="preferences-household-add-input"
              aria-label="New household member name"
            />
            <Button type="button" variant="primary" onClick={addMember}>
              Add
            </Button>
          </div>
        ) : null}
        {localError ? (
          <div role="alert" className="page-alert page-alert-error preferences-alert-spaced">
            {localError}
          </div>
        ) : null}

        <div className="account-prefs-persons-row" role="tablist" aria-label="Household members">
          <button
            type="button"
            role="tab"
            aria-selected={selected === 'you'}
            className={`account-prefs-person-chip${selected === 'you' ? ' active' : ''}`}
            onClick={() => onSelectedChange('you')}
          >
            <span className="account-prefs-person-chip-name">{youName}</span>
            <span className="account-prefs-person-chip-role">{youRole}</span>
            <div className="account-prefs-person-chip-badges">
              {dietBadgesForDisplay(youDietary).map((b) => (
                <span key={b.key} className={b.cls}>
                  {b.text}
                </span>
              ))}
            </div>
          </button>
          {memberKeys.map((name) => {
            const m = members[name] && typeof members[name] === 'object' ? members[name] : {}
            const dietary = m.dietary && typeof m.dietary === 'object' ? m.dietary : {}
            const rel = (m.relationship || '').trim() || 'Family'
            return (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={selected === name}
                className={`account-prefs-person-chip${selected === name ? ' active' : ''}`}
                onClick={() => onSelectedChange(name)}
              >
                <span className="account-prefs-person-chip-name">{name}</span>
                <span className="account-prefs-person-chip-role">{rel}</span>
                <div className="account-prefs-person-chip-badges">
                  {dietBadgesForDisplay(dietary).map((b) => (
                    <span key={b.key} className={b.cls}>
                      {b.text}
                    </span>
                  ))}
                </div>
              </button>
            )
          })}
        </div>

        {selected !== 'you' && members[selected] ? (
          <p className="preferences-caption preferences-caption-spaced">
            Editing <strong>{selected}</strong> only. Your recipe timing, cuisines, store, and how you eat
            are hidden. Select your name above to change those.
          </p>
        ) : null}

        {selected !== 'you' && members[selected] ? (
          <MemberDetailEditor
            name={selected}
            member={members[selected]}
            updateMember={updateMember}
            deleteMember={deleteMember}
          />
        ) : null}
      </div>
    </div>
  )
}

function MemberDetailEditor({ name, member, updateMember, deleteMember }) {
  const m = member && typeof member === 'object' ? member : {}
  const dietary = m.dietary && typeof m.dietary === 'object' ? m.dietary : {}
  const recipe = m.recipe && typeof m.recipe === 'object' ? m.recipe : {}
  const { minutes: maxMinutes, sourceKey: timeSourceKey } = readRecipeMaxMinutes(recipe)
  const weeknightOn = readWeeknight(recipe)
  const cuisineList = readCuisinePreferences(recipe)

  return (
    <div className="preferences-member-detail-card">
      <div className="preferences-member-header">
        <div className="preferences-member-name">{name}</div>
        <button
          type="button"
          className="secondary preferences-delete-btn"
          onClick={() => deleteMember(name)}
          aria-label={`Remove ${name}`}
        >
          Remove
        </button>
      </div>

      <div className="preferences-member-fields">
        <label>
          <span className="preferences-field-label preferences-field-label-block">
            Relationship
          </span>
          <Input
            value={String(m.relationship || '')}
            onChange={(e) => updateMember(name, (next) => ({ ...next, relationship: e.target.value }))}
            placeholder="e.g., Partner"
            className="preferences-field-input-full"
            aria-label={`Relationship for ${name}`}
          />
        </label>
        <label>
          <span className="preferences-field-label preferences-field-label-block">
            Age range
          </span>
          <select
            value={String(m.age_range || '')}
            onChange={(e) => updateMember(name, (next) => ({ ...next, age_range: e.target.value || undefined }))}
            className="input preferences-field-input-full"
            aria-label={`Age range for ${name}`}
          >
            <option value="">(not set)</option>
            <option value="child">child</option>
            <option value="teen">teen</option>
            <option value="adult">adult</option>
            <option value="senior">senior</option>
          </select>
        </label>
      </div>

      <div className="recipe-prefs-field-block">
        <div className="recipe-prefs-label">Recipe</div>
        <label>
          <span className="preferences-field-label preferences-field-label-block">
            Max recipe time (minutes)
          </span>
          <Input
            type="number"
            min={1}
            max={600}
            value={maxMinutes == null ? '' : String(maxMinutes)}
            onChange={(e) =>
              updateMember(name, (next) => ({
                ...next,
                recipe: writeRecipeMaxMinutes(next.recipe || {}, e.target.value),
              }))
            }
            placeholder="e.g., 60"
            className="preferences-field-input-full"
            aria-label={`Max recipe time for ${name}`}
          />
          {timeSourceKey && timeSourceKey !== 'max_total_time_minutes' ? (
            <p className="preferences-caption preferences-caption-tight">
              Detected from saved field: <code>{timeSourceKey}</code>
            </p>
          ) : null}
        </label>

        <div className="preferences-weeknight-row">
          <span className="preferences-field-label preferences-weeknight-label">Prefer weeknight-friendly</span>
          <button
            type="button"
            role="switch"
            aria-checked={weeknightOn}
            aria-label={`Prefer weeknight-friendly recipes for ${name}`}
            className={`account-pref-toggle${weeknightOn ? ' is-on' : ' is-off'}`}
            onClick={() =>
              updateMember(name, (next) => ({
                ...next,
                recipe: writeWeeknight(next.recipe || {}, !weeknightOn),
              }))
            }
          >
            <span className="account-pref-toggle-thumb" aria-hidden="true" />
          </button>
        </div>

        <div className="account-prefs-section preferences-section-spaced">
          <div className="account-prefs-section-label">Cuisines</div>
          {RECIPE_CUISINE_TOGGLES.map((cuisine) => {
            const on = cuisineListHas(cuisineList, cuisine)
            return (
              <div key={cuisine} className="account-prefs-row">
                <div className="account-prefs-row-label">{cuisine}</div>
                <div className="account-prefs-row-control">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={`${on ? 'Disable' : 'Enable'} ${cuisine} cuisine for ${name}`}
                    className={`account-pref-toggle${on ? ' is-on' : ' is-off'}`}
                    onClick={() =>
                      updateMember(name, (next) => {
                        const cur = readCuisinePreferences(next.recipe || {})
                        return {
                          ...next,
                          recipe: writeCuisinePreferences(
                            next.recipe || {},
                            toggleCuisineInList(cur, cuisine, !on),
                          ),
                        }
                      })
                    }
                  >
                    <span className="account-pref-toggle-thumb" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="account-prefs-section preferences-section-spaced">
          <div className="account-prefs-section-label">More cuisines</div>
          <TagInput
            label="Additional cuisine tags"
            values={cuisineList.filter(
              (c) =>
                !RECIPE_CUISINE_TOGGLES.some(
                  (t) => t.toLowerCase() === String(c).toLowerCase(),
                ),
            )}
            onChange={(vals) =>
              updateMember(name, (next) => {
                const cur = readCuisinePreferences(next.recipe || {})
                const fromToggles = RECIPE_CUISINE_TOGGLES.filter((t) =>
                  cuisineListHas(cur, t),
                )
                return {
                  ...next,
                  recipe: writeCuisinePreferences(next.recipe || {}, [
                    ...fromToggles,
                    ...vals,
                  ]),
                }
              })
            }
            placeholder="e.g., Korean"
          />
        </div>
      </div>

      <div className="recipe-prefs-field-block preferences-section-spaced-lg">
        <div className="recipe-prefs-label">Dietary</div>
        <div className="preferences-diet-checkbox-row">
          {[
            ['vegetarian', 'Vegetarian'],
            ['vegan', 'Vegan'],
            ['gluten_free', 'Gluten-free'],
          ].map(([key, label]) => (
            <label key={key} className="preferences-checkbox-label">
              <input
                type="checkbox"
                checked={Boolean(dietary[key])}
                onChange={(e) =>
                  updateMember(name, (next) => ({
                    ...next,
                    dietary: {
                      ...(next.dietary || {}),
                      [key]: e.target.checked,
                    },
                  }))
                }
                aria-label={`${label} for ${name}`}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <div className="preferences-section-spaced">
          <TagInput
            label="Allergies"
            values={dietary.allergies}
            onChange={(vals) =>
              updateMember(name, (next) => ({
                ...next,
                dietary: {
                  ...(next.dietary || {}),
                  allergies: vals,
                },
              }))
            }
            placeholder="e.g., shellfish"
          />
        </div>

        <div className="preferences-section-spaced">
          <TagInput
            label="Dislikes"
            values={dietary.dislikes}
            onChange={(vals) =>
              updateMember(name, (next) => ({
                ...next,
                dietary: {
                  ...(next.dietary || {}),
                  dislikes: vals,
                },
              }))
            }
            placeholder="e.g., mushrooms"
          />
        </div>
      </div>
    </div>
  )
}

export default function PreferencesPage({ onPreferencesChanged }) {
  const { isAuthenticated, isLoading, user, isAdmin } = useAuth()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const [prefs, setPrefs] = useState({})
  const [hasPreferences, setHasPreferences] = useState(false)
  /** 'you' | family member name — controls which preference sections are shown below the household card */
  const [householdTab, setHouseholdTab] = useState('you')

  const baselineRef = useRef(null)

  const normalized = useMemo(() => normalizePreferences(prefs), [prefs])

  useEffect(() => {
    if (householdTab === 'you') return
    const fm = prefs.family_members
    if (!fm || typeof fm !== 'object' || !fm[householdTab]) {
      setHouseholdTab('you')
    }
  }, [prefs.family_members, householdTab])

  async function load() {
    console.log('PreferencesPage.load() called', { isAuthenticated, isLoading })
    if (!isAuthenticated) {
      console.log('PreferencesPage: Not authenticated, skipping load')
      return
    }
    try {
      setLoading(true)
      setError('')
      const url = '/preferences/me'
      console.log('Fetching preferences from:', url, 'isAuthenticated:', isAuthenticated)
      const res = await authenticatedFetch(url, { headers: { ...getAuthHeaders() } })
      
      // Handle authentication errors
      if (res.status === 401 || res.status === 403) {
        console.warn('[PreferencesPage] Authentication error during load')
        setError('Authentication failed. Please refresh the page or log in again.')
        setPrefs({})
        setHasPreferences(false)
        return
      }
      
      console.log('Preferences API response status:', res.status, res.statusText, 'URL:', res.url)
      const data = await res.json().catch(() => ({}))
      console.log('Preferences API response data:', data)
      console.log('Response URL (final after redirects):', res.url)
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      const prefs = data.preferences || {}
      
      // Check if preferences exist - either from API flag or by checking if prefs object has content
      const hasPrefsFromAPI = Boolean(data.has_preferences)
      const hasPrefsFromContent = prefs && typeof prefs === 'object' && Object.keys(prefs).length > 0
      const hasPrefs = hasPrefsFromAPI || hasPrefsFromContent
      
      console.log('Preferences loaded:', { 
        has_preferences_from_api: hasPrefsFromAPI, 
        has_preferences_from_content: hasPrefsFromContent,
        preferences_keys: Object.keys(prefs),
        preferences: prefs,
        raw_api_response: data
      })
      
      // Also log the raw response to see what the API actually returned
      console.log('Raw API response:', JSON.stringify(data, null, 2))
      
      setPrefs(prefs)
      setHasPreferences(hasPrefs)
      const snap = safeStringify(prefs)
      baselineRef.current = snap
    } catch (e) {
      setError(e.message || 'Failed to load preferences')
      setPrefs({})
      setHasPreferences(false)
      baselineRef.current = null
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log('PreferencesPage useEffect triggered', { isLoading, isAuthenticated })
    if (!isLoading && isAuthenticated) {
      console.log('PreferencesPage: Calling load()')
      load()
    }
    if (!isAuthenticated && !isLoading) {
      console.log('PreferencesPage: Not authenticated, clearing preferences')
      setLoading(false)
      setPrefs({})
      setHasPreferences(false)
      setHouseholdTab('you')
      baselineRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading])

  async function save(nextPrefs) {
    try {
      setSaving(true)
      setError('')
      setStatus('')
      const res = await authenticatedFetch('/preferences/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ preferences: nextPrefs || {}, merge: false }),
      })
      
      // Handle authentication errors
      if (res.status === 401 || res.status === 403) {
        console.warn('[PreferencesPage] Authentication error during save')
        setError('Authentication failed. Please refresh the page or log in again.')
        return
      }
      
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      const prefs = data.preferences || {}
      
      // Check if preferences exist - either from API flag or by checking if prefs object has content
      const hasPrefsFromAPI = Boolean(data.has_preferences)
      const hasPrefsFromContent = prefs && typeof prefs === 'object' && Object.keys(prefs).length > 0
      const hasPrefs = hasPrefsFromAPI || hasPrefsFromContent
      
      setPrefs(prefs)
      setHasPreferences(hasPrefs)
      const snap = safeStringify(prefs)
      baselineRef.current = snap
      setStatus('✅ Preferences saved')
      if (onPreferencesChanged) onPreferencesChanged(data)
    } catch (e) {
      setError(e.message || 'Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  const prefsSnapshot = useMemo(() => safeStringify(normalizePreferences(prefs)), [prefs])
  const isDirty =
    baselineRef.current != null && prefsSnapshot !== baselineRef.current

  function revertToSaved() {
    setError('')
    setStatus('')
    const raw = baselineRef.current
    if (!raw) return
    try {
      setPrefs(normalizePreferences(JSON.parse(raw)))
    } catch {
      /* ignore */
    }
  }

  if (isLoading) {
    return (
      <div className="account-preferences-screen">
        <div className="card">
          <p className="page-subtitle preferences-loading-text">Checking authentication…</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  const { minutes: youMinutes, sourceKey: youTimeSourceKey } = readRecipeMaxMinutes(normalized.recipe)
  const cuisineList = readCuisinePreferences(normalized.recipe)
  const weeknightOn = readWeeknight(normalized.recipe)

  return (
    <div className="account-preferences-screen">
      <header className="account-profile-panel-header">
        <h2 className="account-profile-panel-title">Preferences</h2>
        <p className="account-profile-panel-subtitle">
          Recipe settings, dietary needs, and household members
        </p>
      </header>

      {status ? (
        <div role="status" aria-live="polite" className="page-alert page-alert-success">
          <i className="fas fa-check-circle icon-base icon-success" aria-hidden="true" />
          {status}
        </div>
      ) : null}
      {error ? (
        <div role="alert" aria-live="assertive" className="page-alert page-alert-error">
          <i className="fas fa-exclamation-circle icon-base icon-error" aria-hidden="true" />
          {error}
        </div>
      ) : null}

      {!hasPreferences && !loading ? (
        <div className="page-alert page-alert-warning" role="status">
          No saved preferences yet. Adjust settings below and save to create your preference profile.
        </div>
      ) : null}

      {loading ? (
        <div className="card">
          <p className="page-subtitle preferences-loading-text">Loading your preferences…</p>
        </div>
      ) : (
        <>
          <HouseholdMembersPanel
            selected={householdTab}
            onSelectedChange={setHouseholdTab}
            familyMembers={normalized.family_members}
            onChange={(nextFamily) => {
              setPrefs((prev) => {
                const next = normalizePreferences(prev)
                next.family_members = nextFamily || {}
                return next
              })
            }}
            user={user}
            isAdmin={isAdmin}
            youDietary={normalized.dietary}
          />

          {householdTab === 'you' ? (
          <SettingsFormCard
            title={(
              <>
                <i className="fas fa-bowl-food" aria-hidden="true" />
                Recipe Settings
              </>
            )}
            bodyClassName="preferences-form-card-body-flush"
          >
              <div className="account-prefs-section">
                <div className="account-prefs-section-label">Timing</div>
                <div className="account-prefs-row">
                  <div className="account-prefs-row-label">
                    Max recipe time
                    {youTimeSourceKey && youTimeSourceKey !== 'max_total_time_minutes' ? (
                      <span className="account-prefs-ai-badge">
                        <i className="fas fa-wand-magic-sparkles account-prefs-ai-badge-icon" aria-hidden="true" />
                        AI
                      </span>
                    ) : null}
                  </div>
                  <div className="account-prefs-row-control">
                    <input
                      type="number"
                      min={1}
                      max={600}
                      className="account-prefs-input-sm"
                      value={youMinutes == null ? '' : String(youMinutes)}
                      onChange={(e) => {
                        const v = e.target.value
                        setPrefs((prev) => {
                          const next = normalizePreferences(prev)
                          next.recipe = writeRecipeMaxMinutes(next.recipe, v)
                          return next
                        })
                      }}
                      aria-label="Max recipe time in minutes"
                    />
                    <span className="preferences-time-suffix">min</span>
                  </div>
                </div>
                <div className="account-prefs-row">
                  <div className="account-prefs-row-label">Prefer weeknight-friendly</div>
                  <div className="account-prefs-row-control">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={weeknightOn}
                      aria-label="Prefer weeknight-friendly recipes"
                      className={`account-pref-toggle${weeknightOn ? ' is-on' : ' is-off'}`}
                      onClick={() =>
                        setPrefs((prev) => {
                          const next = normalizePreferences(prev)
                          next.recipe = writeWeeknight(next.recipe, !weeknightOn)
                          return next
                        })
                      }
                    >
                      <span className="account-pref-toggle-thumb" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="account-prefs-section">
                <div className="account-prefs-section-label">Cuisines</div>
                {RECIPE_CUISINE_TOGGLES.map((cuisine) => {
                  const on = cuisineListHas(cuisineList, cuisine)
                  return (
                    <div key={cuisine} className="account-prefs-row">
                      <div className="account-prefs-row-label">{cuisine}</div>
                      <div className="account-prefs-row-control">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={on}
                          aria-label={`${on ? 'Disable' : 'Enable'} ${cuisine} cuisine`}
                          className={`account-pref-toggle${on ? ' is-on' : ' is-off'}`}
                          onClick={() =>
                            setPrefs((prev) => {
                              const next = normalizePreferences(prev)
                              const cur = readCuisinePreferences(next.recipe)
                              next.recipe = writeCuisinePreferences(
                                next.recipe,
                                toggleCuisineInList(cur, cuisine, !on),
                              )
                              return next
                            })
                          }
                        >
                          <span className="account-pref-toggle-thumb" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="account-prefs-section">
                <div className="account-prefs-section-label">More cuisines</div>
                <TagInput
                  label="Additional cuisine tags"
                  values={cuisineList.filter(
                    (c) =>
                      !RECIPE_CUISINE_TOGGLES.some(
                        (t) => t.toLowerCase() === String(c).toLowerCase(),
                      ),
                  )}
                  onChange={(vals) =>
                    setPrefs((prev) => {
                      const next = normalizePreferences(prev)
                      const cur = readCuisinePreferences(next.recipe)
                      const fromToggles = RECIPE_CUISINE_TOGGLES.filter((t) =>
                        cuisineListHas(cur, t),
                      )
                      next.recipe = writeCuisinePreferences(next.recipe, [
                        ...fromToggles,
                        ...vals,
                      ])
                      return next
                    })
                  }
                  placeholder="e.g., Korean"
                />
              </div>

              <div className="account-prefs-section">
                <div className="account-prefs-section-label">How you eat</div>
                <div className="preferences-diet-checkbox-row">
                  {[
                    ['vegetarian', 'Vegetarian'],
                    ['vegan', 'Vegan'],
                    ['gluten_free', 'Gluten-free'],
                  ].map(([key, label]) => (
                    <label key={key} className="preferences-checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(normalized.dietary[key])}
                        onChange={(e) =>
                          setPrefs((prev) => {
                            const next = normalizePreferences(prev)
                            next.dietary[key] = e.target.checked
                            return next
                          })
                        }
                        aria-label={label}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <div className="preferences-section-spaced">
                  <TagInput
                    label="Allergies"
                    values={normalized.dietary.allergies}
                    onChange={(vals) =>
                      setPrefs((prev) => {
                        const next = normalizePreferences(prev)
                        next.dietary.allergies = vals
                        return next
                      })
                    }
                    placeholder="e.g., peanuts"
                  />
                </div>
                <div className="preferences-section-spaced">
                  <TagInput
                    label="Dislikes"
                    values={normalized.dietary.dislikes}
                    onChange={(vals) =>
                      setPrefs((prev) => {
                        const next = normalizePreferences(prev)
                        next.dietary.dislikes = vals
                        return next
                      })
                    }
                    placeholder="e.g., spicy food"
                  />
                </div>
              </div>
          </SettingsFormCard>
          ) : null}

          {householdTab === 'you' ? (
          <SettingsFormCard
            className="account-prefs-form-card--store"
            title={(
              <>
                <i className="fas fa-store" aria-hidden="true" />
                Store
              </>
            )}
          >
              <StoreSelector
                variant="full"
                showLabel={true}
                onStoreChange={(store) => {
                  setPrefs((prev) => {
                    const p = normalizePreferences(prev)
                    return {
                      ...p,
                      shopping: {
                        ...p.shopping,
                        preferred_store: store?.name?.trim() ? store.name : '',
                      },
                    }
                  })
                }}
              />
          </SettingsFormCard>
          ) : null}

          <div className="account-prefs-global-actions" role="toolbar" aria-label="Preference actions">
            <button
              type="button"
              className="ghost-link"
              disabled={loading || saving}
              onClick={() => load()}
            >
              <i className="fas fa-rotate" aria-hidden="true" />
              Reload
            </button>
            <button
              type="button"
              className="secondary"
              disabled={loading || saving || !isDirty}
              onClick={revertToSaved}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary"
              disabled={loading || saving}
              onClick={() => save(prefs)}
            >
              {saving ? (
                'Saving…'
              ) : (
                <>
                  <i className="fas fa-floppy-disk" aria-hidden="true" /> Save
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
